<script lang="ts">
  import type { RungKey } from "./lib/rungLadder";
  import type { CvdDisplayMode } from "./lib/lwc/linkedStudyModel";
  import FocusLayout from "./lib/FocusLayout.svelte";
  import { isCandleFocus, readFocusPreferences, writeFocusPreferences } from "./lib/focusLayout.ts";
  import { ownerLinks } from "../headsup-pages.mjs";

  /**
   * THE TWO PANES' TIMEFRAMES LIVE HERE, one $state each.
   * Held by the PARENT, written through by each pane's switcher (`bind:tf`), so the panes stay
   * INDEPENDENT — changing pane A's rung must not move pane B's. The defaults are the pairing
   * v shipped: a 1m working pane beside a 12m context pane.
   */
  /**
   * ⛔ WIDENED FROM `Tf` TO `RungKey` (S4c THE MOUNT). The two bindings below are how the pane's
   * rung crosses into the parent, and while they were `Tf` a SUB-MINUTE rung could not be written
   * back — which is why `RungPills` sat on main, fully built and double-keyed, with zero cockpit
   * mounters. ***THE TYPE ON THIS LINE WAS THE MOUNT'S ACTUAL BLOCKER.***
   * ⚠ The DEFAULTS DO NOT MOVE: minute rungs keep their legacy bare form (`"1"`, `"12"`), so every
   * existing consumer and stored value reads exactly as before. Only sub-minute uses `s:<span>`.
   */
  /*
    ⛔⛔ R836②b — ONE MASTER PER COLUMN. `tfA`/`tfB` USED TO LIVE HERE AND THEY ARE GONE.
    The cockpit had TWO ladders per column with DIFFERENT masters: the chart card's pills wrote
    `rungA`, the CVD pane's own pills wrote `tfA`, and the money flow followed the PANE while the
    V-split followed the CHART. Desk measured it on the served page and the owner said it plainly:
    "timeframes are not working on the TradingView." They were working — each of them moved its
    own master, and neither moved the column.
    ⇒ ***TWO CONTROLS OVER ONE COLUMN IS NOT A CHOICE, IT IS AN AMBIGUITY THE READER HAS TO RESOLVE
    BY EXPERIMENT*** — and the owner resolved it as "broken", which is the correct reading of a
    control whose effect he cannot predict.
    R812① as ruled: THE COLUMN FOLLOWS ITS CHART. So `rungA`/`rungB` below are now the column's
    single rung, read by the chart, the CVD pane, the V-split and the money flow alike.
    ⚠ The two COLUMNS stay independent of each other — that is R771①/SPX-107, and collapsing THEM
    is the mutant GK-aq drove. One master per column is not one master per page.
  */

  /**
   * ⛔ THE PRIMARY PAIR OWNS ITS OWN TWO RUNGS. Reusing `tfA`/`tfB` would couple each new chart
   * to the legacy pane underneath it and move two surfaces from one gesture — precisely the
   * per-chart independence R771① establishes. Defaults preserve the shipped 1m / 12m pairing.
   */
  let rungA = $state<RungKey>("1");
  let rungB = $state<RungKey>("12");
  const CVD_MODE_STORAGE_KEY = "spx-hub.session-cvd.mode";
  const CVD_MODE_EVENT = "spx-hub:cvd-mode";
  let cvdMode = $state<CvdDisplayMode>("continuous");
  function storedCvdMode(): CvdDisplayMode {
    try {
      return localStorage.getItem(CVD_MODE_STORAGE_KEY) === "session" ? "session" : "continuous";
    } catch (error) {
      console.warn(`[App] CVD mode preference read failed — ${String(error)}`);
      return "continuous";
    }
  }
  function setCvdMode(next: CvdDisplayMode) {
    cvdMode = next;
    try {
      localStorage.setItem(CVD_MODE_STORAGE_KEY, next);
    } catch (error) {
      console.warn(`[App] CVD mode preference write failed — ${String(error)}`);
    }
  }
  let focusRangeTicks = $state<8 | 12>(8);
  let focusPreferencesHydrated = $state(false);
  $effect(() => {
    if (!focusPreferencesHydrated) return;
    writeFocusPreferences(undefined, {
      primaryA: rungA,
      primaryB: rungB,
      rangeTicks: focusRangeTicks,
    });
  });
  let chartBWindow = $state<{ from: number; to: number } | null>(null);
  const setChartBWindow = (window: { from: number; to: number } | null) => {
    chartBWindow = window;
  };

  /**
   * SPX Hub shell — P0 ("hello page" with a real chart).
   *
   * Deliberately NOT the full cockpit: the dealer rail, tide panel, news rail, alerts
   * drawer and macro ribbon are P2 (plan §13) and are HUDCHAT's build with the
   * frontend-design → critique → polish → audit sequence. What P0 owes is the shell,
   * the theme system and one live panel — the foundation those panels will sit on.
   */
  import { onMount, tick as svelteTick } from "svelte";
  import { pollIntervalMs, pollDue, isStillFresh } from "./lib/tickPoll";
  import DealerRailPanel from "./lib/DealerRailPanel.svelte";
  import Gex2Panel from "./lib/Gex2Panel.svelte";
  import TidePanel from "./lib/TidePanel.svelte";
  import ExpiryTidePanel from "./lib/ExpiryTidePanel.svelte";
  import NewsPanel from "./lib/NewsPanel.svelte";
  import NetPremPanel from "./lib/NetPremPanel.svelte";
  import FlowRailPanel from "./lib/FlowRailPanel.svelte";
  import DarkpoolRailPanel from "./lib/DarkpoolRailPanel.svelte";
  import TopStrip from "./lib/TopStrip.svelte";
  import TickStatePane from "./lib/TickStatePane.svelte";
  import { fetchTicks, API_BASE, type InstrumentVerdict } from "./lib/api";
  import { leg } from "./lib/feedLeg";
  import { liveMarker, type LiveMarker } from "./lib/livePriceMarker";
  import PrimaryChartPanel from "./lib/PrimaryChartPanel.svelte";
  import RangeChartPanel from "./lib/RangeChartPanel.svelte";
  import MaximizeFrame from "./lib/MaximizeFrame.svelte";
  import { toProbe } from "./lib/tickApi";
  import type { TickProbe } from "./lib/tickState";
  import { assignCells, type MaximizablePanelId, type PanelId } from "./lib/layout";
  import { deriveLandingFold, LANDING_FOLD_WAIT } from "./lib/landingFold";
  import { applyTheme, initTheme, storedChoice, type ThemeChoice, type ResolvedTheme } from "./lib/theme";
  import {
    applyWorkspace, isCollapsed, rememberCollapsed, storedWorkspace, WORKSPACES, type Workspace,
  } from "./lib/workspace";

  let choice = $state<ThemeChoice>("system");
  let resolved = $state<ResolvedTheme>("dark");
  let workspace = $state<Workspace>("brucy");
  let ws = $derived(WORKSPACES[workspace]);

  /*
    ⛔ EVERY id RENDERED VIA cellStyle() MUST BE IN THIS ARRAY. `assignCells` builds the map
    FROM this list, so an id that is rendered but not listed yields `cells[id] === undefined`
    and `cell.col` throws AT BOOT, before a single fetch — the owner sees a permanently blank
    page, not a broken panel.
    ⚠ `PanelId` INCLUDES "flow", so `cellStyle("flow")` type-checks perfectly. svelte-check
    reported 143 files / 0 errors on the broken build.
    ⇒ ***A TYPED KEY IS NOT A POPULATED KEY*** — a Record built from a hand-written list cannot
    be checked by the type that merely names the list's legal members. layout.test.ts arms
    `assignCells`; nothing armed the CALLER's list until now (see App.svelte's arm in
    layout.test.ts: "every cellStyle() id is declared in panelIds").
  */
  /**
   * R771① — TWO interactive charts side by side, each on its OWN rung (the owner's diagram).
   *
   * ⛔⛔ R854② — `paneA`/`paneB` ARE RETIRED HERE, AND THE CONDITION THAT HELD THEM IS DISCHARGED
   * BY MEASUREMENT, NOT BY THE ORDER THAT ASKED FOR IT. R771① retired them *"when the new pair
   * renders with levels ON"*, and the note that stood here warned that deleting them early would
   * strip the walls/flip/VWAPs off the cockpit. **That guard was right, and it is now spent:**
   *   walls + gamma flip .. R814 put them on THIS pair — `PrimaryChartPanel:91-104` holds
   *                         `levels0dte` and derives `renderLevels` through a MEASURED basis
   *                         ratio (SPX levels on an ES axis), fed to the chart at `:334`.
   *   VWAP ................ drawn by NEITHER panel today. `EsChartPanel:1257` hard-sets
   *                         `hasVwap: false, hasAvwap: false` in its own payload because
   *                         *"the spine carries no VWAP column yet"* — so its `showVwap={true}`
   *                         at `:1752` feeds an overlay that has nothing to draw.
   * ⇒ *** A REPLACEMENT IS ONLY A REPLACEMENT ONCE IT CARRIES WHAT IT REPLACES — AND THE HONEST
   * WAY TO SPEND THAT CONDITION IS TO MEASURE EACH THING IT NAMED, NOT TO DELETE THE SENTENCE. ***
   * ⚠ THE VWAP CLAUSE WAS VACUOUS THE WHOLE TIME: it guarded a mark the cockpit never drew.
   * Retiring these panes therefore removes ZERO level information from the owner's screen.
   * ⚠ `EsChartPanel.svelte` IS NOT DELETED — `src/themecheck/ThemeCheck.svelte` mounts it and
   * `check-axis-join`/`check-mounts` read it. It is off the COCKPIT, which is what was asked.
   */
  const panelIds: PanelId[] = ["primaryA", "primaryB", "range", "ladder", "tick", "tide", "tideIndex", "tideEtf", "news", "netprem"];
  const cells = assignCells(panelIds, 3);

  // Order 14: one price pane needs less host than the retired three-pane chart. Browser proof pins
  // the rendered pane above the untouched 313px baseline; 342px leaves the time axis intact.
  const CHART_H = 866;
  const RANGE_CHART_H = 380;
  const initialViewportHeight = typeof window === "undefined" ? Number.MAX_SAFE_INTEGER : window.innerHeight;
  const initialInkScale = 1.12;
  let landingFold = $state(deriveLandingFold(initialViewportHeight, initialInkScale) ?? LANDING_FOLD_WAIT);
  let landingChartHeight = $derived(landingFold.chartHostHeight);
  let landingTideTileHeight = $derived(landingFold.tideTileHeight);
  let maximizedPanel = $state<MaximizablePanelId | null>(null);
  let focusedChart = $derived(isCandleFocus(maximizedPanel) ? maximizedPanel : null);
  let restoreScroll = { x: 0, y: 0 };
  let maximizedChartHeights = $state<Record<"primaryA" | "primaryB" | "range", number>>({
    primaryA: CHART_H,
    primaryB: CHART_H,
    range: RANGE_CHART_H,
  });

  function syncLandingFold() {
    const root = document.documentElement;
    // Remove a prior viewport cap before reading the workspace's requested scale. Otherwise a
    // compact viewport or workspace switch can permanently pin the inline value below its token.
    root.style.removeProperty("--ink-scale");
    const rawRequested = getComputedStyle(root).getPropertyValue("--ink-scale").trim();
    const requestedInkScale = Number(rawRequested);
    if (!Number.isFinite(requestedInkScale) || requestedInkScale <= 0) {
      throw new Error(`App: --ink-scale must resolve to a finite positive number; received ${JSON.stringify(rawRequested)}`);
    }
    const nextFold = deriveLandingFold(window.innerHeight, requestedInkScale);
    if (nextFold === null) return;
    root.style.setProperty("--ink-scale", String(nextFold.inkScale));
    landingFold = nextFold;
  }

  const maximizedChartHeight = (panelId: "primaryA" | "primaryB" | "range", base: number) =>
    maximizedPanel === panelId ? maximizedChartHeights[panelId] : base;
  const fitMaximizedChart = (panelId: "primaryA" | "primaryB" | "range", height: number) => {
    if (maximizedChartHeights[panelId] === height) return;
    maximizedChartHeights = { ...maximizedChartHeights, [panelId]: height };
  };

  async function toggleMaximize(panelId: MaximizablePanelId) {
    if (maximizedPanel === panelId) {
      maximizedPanel = null;
      await svelteTick();
      window.scrollTo(restoreScroll.x, restoreScroll.y);
    } else {
      if (maximizedPanel === null) restoreScroll = { x: window.scrollX, y: window.scrollY };
      maximizedPanel = panelId;
    }
    await svelteTick();
    // Lightweight Charts and the responsive SVG panes re-read their container after the grid flips.
    window.dispatchEvent(new Event("resize"));
  }
  const cellStyle = (id: PanelId) => {
    const cell = cells[id];
    return `grid-column: ${cell.col} / span ${cell.span}; grid-row: ${cell.row} / span ${cell.rowSpan};`;
  };
  const tideCellStyle = (id: "tideIndex" | "tideEtf") =>
    `${cellStyle(id)} --tide-tile-h: ${landingTideTileHeight}px;`;

  /*
    TICK TAPE (SPX-036 family). The pane is PURE — it takes a probe and renders a rung — so the
    fetching, the history and the rung-7 pairing all live here, at the mount.

    ⛔ WHY A HISTORY AND NOT JUST "THE LAST PROBE": rung 7 (rows present and NOT advancing) is
    UNDECIDABLE unless the two samples are >= 60s apart over the SAME window and population. Polling
    every 30s keeps the panel fresh; pairing against the newest probe that is at least 60s OLD keeps
    the comparison legal. Handing `tickRung` the immediately-previous 30s-old probe would make it
    return UNDECIDED forever — correct, and uselessly so.
    ⭐ The alternative (poll every 65s) would satisfy rung 7 and make the panel stale to look at.
    Freshness and decidability want different cadences, so the mount serves both instead of picking.
  */
  const TICK_WINDOW_S = 300;
  const TICK_POPULATION = "schema='trades' AND side IS NOT NULL";
  const SPX_LIVE_POLL_MS = 30_000;
  /** Re-grades held tick freshness between fetches; it does not make another network request. */
  const FRESHNESS_HEARTBEAT_MS = 1_000;
  /**
   * ⛔ The cadence is NOT a literal here any more. It is `lib/tickPoll`, so an arm can execute
   * the decision — a dark switch beside a hard-coded number is indistinguishable from no fix.
   * Ships DARK by DESK-n12's amended Decision A; the flip-on is a desk word, not a knob.
   */
  const RUNG7_MIN_SPACING_MS = 60_000;

  let probe = $state<TickProbe | null>(null);
  let tickPayload = $state<import("./lib/tickApi").TicksPayload | null>(null);
  /** Wall-clock at which `tickPayload` arrived. The freshness guard needs elapsed-since-fetch. */
  let tickFetchedAtMs = $state<number | null>(null);
  let tickNowMs = $state(Date.now());
  /**
   * ⛔ NAMED, not repeated. The identical expression `tickPayload?.last_price ?? null` previously
   * appeared TWICE in this file — once for the chart's forming bar, once for the headline — and
   * GK-s's H2 mutant COULD NOT RUN because the harness refused to guess which occurrence to
   * mutate. ⇒ *A MUTANT THAT DOES NOT APPLY IS A HARNESS FAILURE, NEVER A VERDICT*, and the
   * ambiguity was mine to remove. One name per consumer, so an arm can say which wiring it broke.
   */
  let headlineTickPrice = $derived(tickPayload?.last_price ?? null);
  let chartTickPrice = $derived(tickPayload?.last_price ?? null);
  let chartTickMs = $derived.by(() => {
    const vendorTs = tickPayload?.last_price_vendor_ts;
    if (typeof vendorTs !== "string" || vendorTs.length === 0) return null;
    const parsed = Date.parse(vendorTs);
    return Number.isFinite(parsed) ? parsed : null;
  });
  let chartTickFresh = $derived(isStillFresh(
    tickPayload?.last_price_age_s ?? null,
    tickFetchedAtMs === null ? Number.POSITIVE_INFINITY : Math.max(0, tickNowMs - tickFetchedAtMs),
  ));
  let probeHistory = $state<TickProbe[]>([]);
  let tickError = $state<string | null>(null);
  /** The newest probe at least 60s older than the current one, or null if we have no legal pair yet. */
  let prevProbe = $derived(
    probe === null
      ? null
      : (probeHistory.filter((p) => probe!.asOfMs - p.asOfMs >= RUNG7_MIN_SPACING_MS).at(-1) ?? null),
  );

  /** The one-bar SPX spine envelope. Kept local because `api.ts::CandlesResponse` promises fields
      only after admission, while this path MUST remain able to describe a refused or partial leg. */
  interface SpxPriceEnvelope {
    symbol?: string;
    vendor_ts?: string | null;
    budget_s?: number | null;
    venue_state?: { verdict?: string | null };
    bars?: { c?: number }[];
  }
  let livePrice = $state<LiveMarker>({
    kind: "absent",
    reason: "the shared SPX live-price leg has not answered yet",
  });
  let livePriceRequestGeneration = 0;
  let dealerGexReport = $state<InstrumentVerdict | null>(null);
  const receiveDealerGexReport = (report: InstrumentVerdict | null) => { dealerGexReport = report; };

  /**
   * SPX EXACT, NEVER ES. This mount owns the URL, admission and cadence so §GEX-2 and §STRUCTURE
   * consume one marker from one request. No child can quietly switch `/api/es/last` in as an
   * approximation when SPX is absent, and adding a second consumer cannot double the data spend.
   */
  async function fetchSpxLivePrice(): Promise<LiveMarker> {
    const result = await leg(
      `${API_BASE}/api/es/candles?symbol=SPX&limit=1&tf=1`,
      "dealer-live-price",
    );
    const body = result.body as SpxPriceEnvelope | null;
    const newest = Array.isArray(body?.bars) ? body.bars.at(-1) : undefined;
    const parsedAsOf = typeof body?.vendor_ts === "string" ? Date.parse(body.vendor_ts) : Number.NaN;

    // ⛔ A 200 FROM THE WRONG SYMBOL IS STILL THE WRONG INSTRUMENT. This guard is deliberately
    // additional to the URL: an old route that ignores `symbol=SPX` must not turn ES into a line.
    const wrongSymbol = result.transport === null && body?.symbol !== "SPX"
      ? `SPX live price route served ${body?.symbol ?? "no symbol"}; ES fallback is forbidden`
      : null;
    const marker = liveMarker({
      price: typeof newest?.c === "number" ? newest.c : null,
      asOfMs: Number.isFinite(parsedAsOf) ? parsedAsOf : null,
      nowMs: Date.now(),
      budgetS: typeof body?.budget_s === "number" ? body.budget_s : Number.NaN,
      source: "SPX spine · 1m",
      transport: result.transport ?? wrongSymbol,
    });

    // `leg()` already logs transport failures. Shape, clock and budget refusals happen after that
    // boundary, so they need their own line or the visible absence would have no diagnostic twin.
    if (marker.kind === "absent" && result.transport === null
      && body?.venue_state?.verdict !== "CLOSED") {
      console.error(`[dealer-live-price] REFUSED — ${marker.reason}`);
    }
    return marker;
  }

  async function loadSharedSpxLivePrice() {
    const mine = ++livePriceRequestGeneration;
    try {
      const nextLive = await fetchSpxLivePrice();
      if (mine === livePriceRequestGeneration) livePrice = nextLive;
    } catch (err) {
      const reason = err instanceof Error ? err.message : String(err);
      console.error(`[dealer-live-price] LOAD FAILURE — ${reason}`);
      if (mine === livePriceRequestGeneration) livePrice = { kind: "absent", reason };
    }
  }

  let tickPollInFlight = false;
  let tickPollsSkipped = $state(0);

  async function loadTicks() {
    try {
      const payload = await fetchTicks("ES1!", 10);
      tickError = null;
      tickPayload = payload;
      tickFetchedAtMs = Date.now();
      const next = toProbe(payload, { windowS: TICK_WINDOW_S, population: TICK_POPULATION });
      if (probe) probeHistory = [...probeHistory, probe].slice(-20);
      probe = next;
    } catch (err) {
      // Fail LOUD. The pane's own NO FEED rung is the render; the error rides the chip.
      tickError = err instanceof Error ? err.message : String(err);
      tickPayload = null;
      tickFetchedAtMs = null;
      probe = toProbe(null, { windowS: TICK_WINDOW_S, population: TICK_POPULATION });
    }
  }

  onMount(() => {
    // Workspace first: it stamps the density attribute the layout reads. Theme is restored after,
    // so a theme the user chose explicitly survives a reload instead of being re-stamped away.
    workspace = storedWorkspace();
    applyWorkspace(workspace);
    resolved = initTheme();
    choice = storedChoice();
    const focusPreferences = readFocusPreferences();
    rungA = focusPreferences.primaryA;
    rungB = focusPreferences.primaryB;
    focusRangeTicks = focusPreferences.rangeTicks;
    focusPreferencesHydrated = true;
    cvdMode = storedCvdMode();
    const syncCvdMode = (event: Event) => setCvdMode((event as CustomEvent<CvdDisplayMode>).detail);

    const restoreOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && maximizedPanel !== null) void toggleMaximize(maximizedPanel);
    };
    syncLandingFold();
    window.addEventListener("keydown", restoreOnEscape);
    window.addEventListener("resize", syncLandingFold);
    window.addEventListener(CVD_MODE_EVENT, syncCvdMode);

    let lastTickPollAt = 0;
    const pollTicks = () => {
      const now = Date.now();
      const verdict = (tickPayload as { venue_state?: { verdict?: string | null } } | null)?.venue_state?.verdict;
      if (!pollDue(now, lastTickPollAt, verdict)) return;
      if (tickPollInFlight) { tickPollsSkipped += 1; return; }
      lastTickPollAt = now;
      tickPollInFlight = true;
      void loadTicks().finally(() => { tickPollInFlight = false; });
    };
    pollTicks();
    void loadSharedSpxLivePrice();
    const tickTimer = setInterval(pollTicks, pollIntervalMs());
    const freshnessTimer = setInterval(() => (tickNowMs = Date.now()), FRESHNESS_HEARTBEAT_MS);
    // One request, one marker, two consumers (§GEX-2 and §STRUCTURE).
    const livePriceTimer = setInterval(() => void loadSharedSpxLivePrice(), SPX_LIVE_POLL_MS);
    return () => {
      clearInterval(tickTimer);
      clearInterval(freshnessTimer);
      clearInterval(livePriceTimer);
      window.removeEventListener("keydown", restoreOnEscape);
      window.removeEventListener("resize", syncLandingFold);
      window.removeEventListener(CVD_MODE_EVENT, syncCvdMode);
    };
  });

  function setTheme(next: ThemeChoice) {
    choice = next;
    resolved = applyTheme(next);
    // Tell the canvas layers to re-resolve their colour snapshots (see theme.ts).
    window.dispatchEvent(new CustomEvent<ResolvedTheme>("spx-theme", { detail: resolved }));
  }

  /**
   * Selecting a workspace stamps ITS theme — that is the §13 behaviour and the mock's. What it must
   * not do is pretend the workspace *is* the theme: the control now tracks the stored workspace, so
   * switching to dark inside Scott's workspace no longer makes the UI claim you are Brucy.
   */
  function setWorkspace(who: Workspace) {
    workspace = who;
    const cfg = applyWorkspace(who);
    syncLandingFold();
    setTheme(cfg.defaultTheme);
  }
</script>

<header class="top">
  <span class="wordmark">SPX<b>·</b>HUB</span>
  <span class="pagechip">ES COMMAND</span>
  <nav class="tabs">
    <button class="tab on">COCKPIT</button>
    <!--
      ⛔ DERIVED FROM THE REGISTRY, NOT LISTED HERE. Two hand-written anchors is how `spike` came to
      be BUILT, GATED and UNREACHABLE while a cure for exactly that disease was being keyed one row
      away. ⇒ ***A NEGATIVE LIST ARMS THE INSTANCES; ONLY A POSITIVE ONE ARMS THE CLASS.***
      `ownerVisible` is what keeps the R389 spike off this row — a DECLARATION, auditable, rather
      than an omission nobody can tell from an oversight.

      ⛔ AN ANCHOR, NOT A BUTTON (6's reasoning, kept): it navigates to a different document, so a
      <button> would need JS to do what the platform already does and would lose middle-click,
      cmd-click and open-in-new-window — the affordances that let this sit on a second screen.
      `rel=noopener` because `_blank` otherwise hands the new page a `window.opener` handle back.

      ⚠ AND GK-zz's COND-1, CURED HERE: `target=_blank` was UNANNOUNCED to assistive tech — the ↗ is
      aria-hidden, so a screen-reader user got no warning the link leaves the page. The visually
      hidden span says so in words. ***A GLYPH THAT IS HIDDEN FROM AT IS NOT A WARNING, IT IS
      DECORATION.***
    -->
    {#each ownerLinks() as page (page.id)}
      <a
        class="tab link"
        href={page.href}
        target="_blank"
        rel="noopener noreferrer"
        title={page.title}>
        {page.label}
        <span class="ext" aria-hidden="true">↗</span>
        <span class="sr-only">(opens in a new tab)</span>
      </a>
    {/each}
    <button class="tab locked" title="Unlocks when E1–E4 verdicts land" disabled>
      IDEAS · locked until E1–E4
    </button>
    <button class="tab locked" title="Gated execution ladder — shadow → paper → council keys" disabled>
      EXECUTE · gated
    </button>
  </nav>
  <span class="spacer"></span>
  <span class="p0band">P0 SCAFFOLD · SPX-002</span>

  <div class="who" role="group" aria-label="Workspace">
    {#each Object.values(WORKSPACES) as w (w.id)}
      <button
        class:on={workspace === w.id}
        aria-pressed={workspace === w.id}
        title={w.summary}
        onclick={() => setWorkspace(w.id)}>{w.label}</button>
    {/each}
  </div>

  <div class="themeswitch" role="group" aria-label="Theme">
    <button class:on={choice === "light"} onclick={() => setTheme("light")} title="Force light">☀</button>
    <button class:on={choice === "system"} onclick={() => setTheme("system")} title="Follow system">◐</button>
    <button class:on={choice === "dark"} onclick={() => setTheme("dark")} title="Force dark">☾</button>
  </div>
</header>

<!-- §13's TOP STRIP (SPX-018). Sits between the shell chrome and the cockpit grid, spanning the
     full width — it is a status ribbon, not a panel, and it is outside <main> so the grid does not
     have to carry a row that is not a card. -->
<!--
  SPX-061 clause (2): the headline reads the TICK price. Fed from the SAME `/api/es/ticks` payload
  the pane already polls — no new poll, no new connection, and no caller-controlled `?symbol=`
  (BUILDER-ff's live seq-scan defect, 2026-08-12 07:26Z, until his allowlist lands).
  ⚠ `last_price_age_s` travels WITH the price: the strip refuses a price it cannot place in time.
-->
<TopStrip
  tickPrice={headlineTickPrice}
  tickMs={chartTickMs}
  tickAgeS={tickPayload?.last_price_age_s ?? null} />

<main
  data-polls-skipped={tickPollsSkipped}
  class:maximize-active={maximizedPanel !== null}
  class:focus-active={focusedChart !== null}
  data-maximized-panel={maximizedPanel ?? undefined}>
  {#if focusedChart !== null}
    <FocusLayout
      api={API_BASE}
      chart={focusedChart}
      rung={focusedChart === "primaryA" ? rungA : rungB}
      onRungChange={(next) => {
        if (focusedChart === "primaryA") rungA = next;
        else rungB = next;
      }}
      {livePrice}
      chartWindow={chartBWindow}
      onChartWindowChange={setChartBWindow}
      tick={{ price: chartTickPrice, ms: chartTickMs, fresh: chartTickFresh }}
      onexit={() => void toggleMaximize(focusedChart)} />
  {/if}
  <div class="cockpit-panels" class:cockpit-hidden={focusedChart !== null}>
  <!-- Layout v2: every child receives a computed cell. No panel is left to browser auto-placement. -->
  <!-- ONE tick source on the page: the mount fetches it once and both panels read the same price. -->
  <!--
    ORDER 14 — THE PRIMARY INTERACTIVE CHART is PRICE ONLY, zoom/pan/scroll live. Its admitted
    CVD, V-split, and money-flow studies render in a sibling card inside the same column and follow
    this chart's rung without adding another fetch owner.
  -->
  <!--
    R771① — the pair. Two instances of the SAME proven vehicle on DIFFERENT rungs, so the owner
    reads 1m against 12m the way the old pane pair let him. `SessionCvdPane`'s module scope is
    `const` only (measured), so two instances cannot collide.
    ⚠ Each names ITSELF via its host, root + rung — see chartLabel.test.ts. Two charts titled
    identically would be worse than one.
  -->
  <MaximizeFrame
    panelId="primaryA" panelClass="primary primary-a" cellStyle={cellStyle("primaryA")}
    maximized={maximizedPanel === "primaryA"}
    hidden={maximizedPanel !== null && maximizedPanel !== "primaryA"}
    fitSelector=".host" onfit={(height) => fitMaximizedChart("primaryA", height)}
    ontoggle={() => void toggleMaximize("primaryA")}>
    <div class="maximized-chart-host">
      <PrimaryChartPanel
        api={API_BASE} bind:rung={rungA} limit={300}
        height={maximizedChartHeight("primaryA", landingChartHeight)}
        chartId="primaryA"
        cvdMode={cvdMode} onCvdModeChange={setCvdMode}
        slot="CHART A · " stackMarker="CHART A LINKED STUDIES"
        chartRole="lower" activeWindow={chartBWindow}
        tickPrice={chartTickPrice} tickMs={chartTickMs} tickFresh={chartTickFresh} />
    </div>
  </MaximizeFrame>
  <MaximizeFrame
    panelId="primaryB" panelClass="primary primary-b" cellStyle={cellStyle("primaryB")}
    maximized={maximizedPanel === "primaryB"}
    hidden={maximizedPanel !== null && maximizedPanel !== "primaryB"}
    fitSelector=".host" onfit={(height) => fitMaximizedChart("primaryB", height)}
    ontoggle={() => void toggleMaximize("primaryB")}>
    <div class="maximized-chart-host">
      <PrimaryChartPanel
        api={API_BASE} bind:rung={rungB} limit={300}
        height={maximizedChartHeight("primaryB", landingChartHeight)}
        chartId="primaryB"
        cvdMode={cvdMode} onCvdModeChange={setCvdMode}
        slot="CHART B · " stackMarker="CHART B LINKED STUDIES"
        chartRole="higher" onVisibleWindowChange={setChartBWindow}
        tickPrice={chartTickPrice} tickMs={chartTickMs} tickFresh={chartTickFresh} />
    </div>
  </MaximizeFrame>
  <!-- ORDER 28: the compact synthetic-time range chart spans the A/B pair before the tide row.
       It owns only the already-served range and 0DTE-level reads; it never reaches the tick writer. -->
  <MaximizeFrame
    panelId="range" panelClass="range" cellStyle={cellStyle("range")}
    maximized={maximizedPanel === "range"}
    hidden={maximizedPanel !== null && maximizedPanel !== "range"}
    fitSelector=".chart-host" onfit={(height) => fitMaximizedChart("range", height)}
    ontoggle={() => void toggleMaximize("range")}>
    <RangeChartPanel api={API_BASE}
      height={maximizedChartHeight("range", RANGE_CHART_H)} />
  </MaximizeFrame>
  <!--
    THE MM EXPOSURE RAIL — ONE RAIL, TWO LABELLED SECTIONS (blueprint §1; DESK-n21 R161 on the
    owner's word "the new API market exposure panel ALONGSIDE our existing exposure panel").

    §STRUCTURE is where dealer gamma SITS (daily capture — position). §FLOW is what dealers are
    DOING minute by minute (the upgraded API — motion). Structure is the map; flow is the traffic.
    ⛔ THEY ARE NOT MERGED AND THEY ARE NOT TWO RAILS. Merging two different quantities into one
    visual is the mis-label law; splitting them into two rails wastes the chart's width. Each
    section carries its OWN heading, its OWN staleness chip and its OWN source label, because a
    reader who cannot tell which section a number came from has two numbers and one meaning.
  -->
  <div class="cell ladder" data-panel-mount="ladder" style={cellStyle("ladder")}>
    <span class="alignment">ALIGNED: 1m</span>
    <!--
      ⛔ §GEX-2 RENDERS ABOVE §STRUCTURE — R387/R408's two-sections law made visible: the DELIVERY
      (today's 0DTE, minute cadence) sits on top; the MAP (pooled all-expiry, daily capture) below.
      A reader who does not know the difference must be able to read it off the ORDER.
    -->
    <Gex2Panel {livePrice} dealerReport={dealerGexReport} />
    <h3 class="railsec">§STRUCTURE <small>dealer gamma — position · all-expiry</small></h3>
    <DealerRailPanel rows={ws.ladderRows} {livePrice} fixedScope="all_expiry"
      onReport={receiveDealerGexReport} />
    <h3 class="railsec">§FLOW <small>dealer motion — minute delta and vega flow</small></h3>
    <FlowRailPanel root="SPX" />
    <h3 class="railsec">§DARK POOL <small>SPY dark-pool and lit price levels</small></h3>
    <DarkpoolRailPanel />
  </div>

  <!--
    The tick tape's state. It sits beside the chart because it answers a question ABOUT the chart's
    source, and because the rung it is on is the first thing to check when the tape looks wrong.
    ⛔ It renders a STATE, never a series — the CVD aggregator is BUILDER-z's and the route says so
    itself: `cvd_state: ABSENT`. An absent CVD and a zero CVD are different facts.
  -->
  <div class="cell tick" data-panel-mount="tick" style={cellStyle("tick")}>
    <TickStatePane {probe} prev={prevProbe} error={tickError} payload={tickPayload} />
  </div>

  <!--
    TIDE (SPX-009). §13 puts it on the second row beside the news rail — and the news rail is now
    BUILT (SPX-044's panel half, below), so the tide takes the chart's column and the rail sits
    beside it, exactly the "eventually takes the other half" this file's own CSS comment promised.

    MOUNTED 2026-08-07 21:4xZ, and NOT before — the row was held open for a day because the panel
    could not tell a shut market from a broken feed for its own instrument. That is now server-side
    per instrument (SPX-010, deployed 21:34Z): MARKET serves CLOSED with stale=true simultaneously,
    SPY serves LIVE on its own 09:30→20:00 arrival window. Verified from the edge before mounting,
    both conditions, not just the one that unblocked it.

    IN BOTH WORKSPACES ON PURPOSE — Q6, confirmed against the ratified mock's own comment ("tide
    STAYS — it's Scott's primary read"). §13's prose says Scott gets it collapsed; the mock and the
    merge gate disagree and they are the two closest to the screen. `workspace.ts` collapses only
    `news` and `flow`, so no registration is needed here for that to hold.
  -->
  <MaximizeFrame
    panelId="tide" panelClass="tide" cellStyle={cellStyle("tide")}
    maximized={maximizedPanel === "tide"}
    hidden={maximizedPanel !== null && maximizedPanel !== "tide"}
    ontoggle={() => void toggleMaximize("tide")}>
    <TidePanel />
  </MaximizeFrame>

  <!--
    NEWS (SPX-044, panel half). §13's news rail, beside the tide on its row. The `news` slot has
    been registered in workspace.ts's `collapsed` list since the workspaces landed — Scott starts
    it collapsed, Brucy open — and re-selecting a workspace re-stamps that choice, same semantics
    as the workspace → theme stamp above. The panel polls its own route (~60s against a 120s
    budget); nothing here re-reads another panel's feed.
  -->
  <!--
    R812①'s standalone per-column money flow (`flowA`/`flowB`, MoneyFlowPanel) is RETIRED —
    owner word, 2026-08-27: both instances read `MONEY FLOW UNAVAILABLE / unsupported tf '1'` on
    the served page and were removed. The PER-PANE MF LANE is a different object and is unaffected:
    R312-AMENDED-2 moved that waveform INSIDE `EsChartPanel` ("LANE 3") on each pane's own rung, and
    ORDER 7 draws a money-flow WAVE inside the primary chart's own pane 2 — that is the money flow
    the owner still gets, just not as a separate standalone panel below the chart.
    ⚠ Left here as the pointer, so a future reader does not re-add a standalone instance believing
    nothing watches for it — `layout.test.ts`'s R812① arms assert this absence explicitly.
  -->

  <!-- DEFAULT-COCKPIT-1: the existing two cells each hold two pinned scopes, so INDEX 0DTE ·
       INDEX W · ETF 0DTE · ETF W read as one four-tile row without inventing a panel id. -->
  <MaximizeFrame
    panelId="tideIndex" panelClass="tide-expiry tide-index" cellStyle={tideCellStyle("tideIndex")}
    maximized={maximizedPanel === "tideIndex"}
    hidden={maximizedPanel !== null && maximizedPanel !== "tideIndex"}
    ontoggle={() => void toggleMaximize("tideIndex")}>
    <ExpiryTidePanel tide="index" title="Index 0DTE Tide" initialScope="zero_dte" pinnedScope={true} />
    <ExpiryTidePanel tide="index" title="Index Weekly Tide" initialScope="weekly" pinnedScope={true} />
  </MaximizeFrame>

  <MaximizeFrame
    panelId="tideEtf" panelClass="tide-expiry tide-etf" cellStyle={tideCellStyle("tideEtf")}
    maximized={maximizedPanel === "tideEtf"}
    hidden={maximizedPanel !== null && maximizedPanel !== "tideEtf"}
    ontoggle={() => void toggleMaximize("tideEtf")}>
    <ExpiryTidePanel tide="etf" title="ETF 0DTE Tide" initialScope="zero_dte" pinnedScope={true} />
    <ExpiryTidePanel tide="etf" title="ETF Weekly Tide" initialScope="weekly" pinnedScope={true} />
  </MaximizeFrame>

  <div class="cell news" data-panel-mount="news" style={cellStyle("news")}>
    <!--
      R717① pinned prefs (R753⑧ / R763⑦). `isCollapsed` now consults THE READER'S stored choice
      and falls back to the workspace default; `onToggle` fires only on a MANUAL fold, so a
      workspace re-stamp is never written back as if it were a preference.
    -->
    <NewsPanel
      startCollapsed={isCollapsed(ws, "news")}
      onToggle={(collapsed) => rememberCollapsed(workspace, "news", collapsed)}
    />
  </div>

  <!--
    NET-PREMIUM (R677①, R705④). ⛔ MOUNTED BECAUSE R519 SAYS WIRE THE MOUNT FIRST, and because
    GK-ai measured what unmounted actually meant here: `NetPremPanel` had ZERO importers in the
    whole app (control: EsChartPanel 19), so AGENTS.md law 8's mandatory both-theme check had
    never run on it and no cockpit pixel could ever have appeared. A reserved surface with no
    mounter is a detection leg with no consumer — the `flow` slot's own lesson, one panel over.

    ⚠ IT WILL RENDER AN HONEST BUT UNFLATTERING PANEL TODAY, AND THAT IS THE POINT. The route
    serves 390 buckets of which ** 0 are live **, newest tape 2026-08-21, and its `age_s` tracks
    CAPTURE not DATA — so the pane shows the tide, "0 live · 390 backfill", and a loud warning
    that the capture clock is ~63h younger than the data. Law 1: that state must be visible, not
    absent. It is a CAPTURE fault and the panel says so rather than blaming the market.
  -->
  <div class="cell netprem" data-panel-mount="netprem" style={cellStyle("netprem")}>
    <NetPremPanel />
  </div>
  </div>
</main>

<style>
  /* Section headings for the two-section exposure rail. Deliberately quiet: they are
     WAYFINDING, not chrome — the reader needs to know which section a number belongs to. */
  .railsec {
    margin: 6px 0 2px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.04em;
    color: var(--muted);
  }
  .railsec small { font-weight: 400; letter-spacing: 0; opacity: 0.85; }
  .top {
    display: flex;
    align-items: center;
    gap: 18px;
    padding: 10px 20px;
    border-bottom: 1px solid var(--line);
    background: var(--surface);
    flex-wrap: wrap;
  }
  .wordmark {
    font-weight: 700;
    letter-spacing: 0.14em;
    font-size: 15px;
  }
  .wordmark b {
    color: var(--accent);
  }
  .pagechip {
    font-size: 11px;
    letter-spacing: 0.18em;
    color: var(--muted);
  }
  .tabs {
    display: flex;
    gap: 6px;
    margin-left: 8px;
  }
  .tab {
    padding: 5px 12px;
    border-radius: var(--radius-pill);
    font-size: 11px;
    letter-spacing: 0.04em;
    border: 1px solid transparent;
    cursor: pointer;
    color: var(--muted);
    background: transparent;
  }
  .tab.on {
    background: var(--chip);
    color: var(--ink);
    border-color: var(--line);
  }
  .tab.locked {
    opacity: 0.55;
    cursor: not-allowed;
  }
  /* The one tab that is a LINK. Matches the tab shape exactly so the row reads as one control
     group, but carries a border at rest so it does not look disabled next to `.locked`, and an
     explicit `text-decoration: none` because an <a> brings an underline that no other tab has. */
  .tab.link {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    text-decoration: none;
    border-color: var(--line);
    color: var(--muted);
  }
  .tab.link:hover,
  .tab.link:focus-visible {
    background: var(--chip);
    color: var(--ink);
  }
  /* ⛔ Keyboard users must SEE the focus. The tab row is the first thing Tab reaches. */
  .tab.link:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }
  .ext {
    font-size: 10px;
    opacity: 0.75;
  }
  /* Visible to assistive tech, never on screen. Not `display:none` — that would hide it from AT
     too, which is the whole defect this cures. */
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    /* `clip-path: inset(50%)`, NOT the legacy `clip` rectangle form. Two reasons: `clip` is
       deprecated, AND its zero-argument literal trips `tickPoll.test.ts`'s timer arm, whose
       `[\s\S]*?` spans the whole file — measured, a 12.8k-char match running from a CORRECT
       setInterval to a CSS rule 300 lines below it. Rowed as a harness false-positive; deliberately
       NOT patched here, because that arm belongs to another object and weakening a guard to pass
       my own change is the defect this estate keeps banking. */
    clip-path: inset(50%);
    white-space: nowrap;
    border: 0;
  }
  .spacer {
    flex: 1;
  }
  .p0band {
    font-size: 11px;
    letter-spacing: 0.14em;
    color: var(--stale-ink);
    border: 1px solid var(--stale);
    border-radius: 4px;
    padding: 2px 8px;
  }
  .who,
  .themeswitch {
    display: flex;
    border: 1px solid var(--line);
    border-radius: var(--radius-pill);
    overflow: hidden;
  }
  .who button,
  .themeswitch button {
    border: 0;
    background: transparent;
    color: var(--muted);
    padding: 5px 14px;
    font-size: 11px;
    cursor: pointer;
    font-weight: 600;
    letter-spacing: 0.05em;
  }
  .who button.on,
  .themeswitch button.on {
    background: var(--accent);
    /* --on-accent, not #fff: white is 3.02:1 on the dark theme's accent (AA fail at 12px). */
    color: var(--on-accent);
  }
  main,
  .foot {
    --cockpit-max-w: 3440px;
    max-width: var(
      --cockpit-max-w
    );
  }
  main {
    margin: 14px auto;
    padding: 0 20px;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(240px, var(--rail-w));
    align-items: start;
    gap: 14px;
  }
  main.maximize-active {
    position: fixed;
    inset: 0;
    z-index: 10;
    box-sizing: border-box;
    height: 100dvh;
    display: block;
    max-width: none;
    margin: 0;
    padding-block: 14px;
    overflow: hidden;
    background: var(--bg);
  }
  main.focus-active { padding: 14px; }
  main.maximize-active.focus-active {
    display: grid;
    grid-template-areas:
      "chart chart chart chart rail"
      "tide tide tide tide rail";
    grid-template-columns: repeat(4, minmax(0, 1fr)) minmax(360px, 1.56fr);
    grid-template-rows: minmax(520px, 1fr) minmax(240px, auto);
    gap: 14px;
  }
  /**
   * FOCUS-FIT-1 breakpoint: below 1440px the four pinned expiry tides fold to two rows of two,
   * then the measured dealer column drops underneath. The linked chart remains the first row.
   */
  @media (max-width: 1439px) {
    main.maximize-active.focus-active {
      grid-template-areas:
        "chart chart"
        "tide tide"
        "tide tide"
        "rail rail";
      grid-template-columns: repeat(2, minmax(0, 1fr));
      grid-template-rows: minmax(520px, auto) repeat(2, minmax(240px, auto)) auto;
    }
  }
  main.maximize-active :global(.maximize-frame.maximized) {
    box-sizing: border-box;
    height: 100%;
    min-height: 0;
    max-height: 100%;
    overflow: hidden;
  }
  main.maximize-active > :global(.cell:not(.maximized)),
  main.maximize-active > :global(.cockpit-panels) > :global(.cell:not(.maximized)) { display: none; }
  .cockpit-panels { display: contents; }
  .cockpit-panels { gap: inherit; }
  .cockpit-panels.cockpit-hidden { display: none; }
  .cell { min-width: 0; }
  :global(.cell.primary) {
    align-self: stretch;
    display: flex;
    flex-direction: column;
  }
  .maximized-chart-host {
    min-width: 0;
    min-height: 0;
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
  }
  /* ORDER 13: the dealer rail must paint the same panel surface as the chart canvases. Leaving
     this cell transparent exposes --bg, which is darker than --surface in both themes.
     R2006 inverts chain 123 for LANDING-RAIL-FIT-1 9bde9516 only: its 118/135/138 px panels made
     content sizing safe, but the served ladder is now 1078 px. Size containment keeps that content
     out of track sizing while stretch derives the rail's height from the chart and tide rows. */
  .cell.ladder {
    background: var(--surface);
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    min-height: 0;
    contain: size;
    align-self: stretch;
    overflow: hidden;
  }
  :global(.cell.ladder > section) { min-height: 0; overflow: hidden; }
  :global(.cell.ladder > section.gex2),
  :global(.cell.ladder > section.card) { flex: 1 1 0; }
  :global(.cell.ladder > section.flowrail),
  :global(.cell.ladder > section.darkpool-rail) { flex: 0 0 auto; }
  :global(.cell.ladder .body),
  :global(.cell.ladder .laddermeasure) {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-height: 0;
    overflow: hidden;
  }
  :global(.cell.ladder .ladderview) {
    flex: 1 1 auto;
    min-height: 0;
    max-height: 100%;
    overflow-y: auto;
  }
  :global(.cell.tide-expiry) {
    --tide-tile-h: 240px;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: inherit;
  }
  :global(.cell.tide-expiry .expiry-tide.focus-scope .scope) {
    grid-template-rows: var(
      --tide-tile-h
    ) !important;
  }
  :global(.cell.tide-expiry .expiry-tide.focus-scope svg) {
    height: var(
      --tide-tile-h
    ) !important;
  }
  /*
    ⛔⛔ R854② — `.pane` AND ITS SCROLL CAP ARE GONE BECAUSE THEIR ONLY CELLS ARE GONE.
    R836① capped `.pane :global(.card > .body)` at `--pane-body-max` so the CVD pane's ~640px of
    honest fail-LOUD chrome (a `loud` status strip, THREE `lvlfault` notices, the money-flow
    waveform, the cvd2 section) scrolled instead of pushing the V-split to y=1842. That rule was
    right and it is now UNREACHABLE: `.cell.pane` is not rendered any more, so these selectors
    matched nothing.
    ⇒ *** DEAD CSS IS NOT HARMLESS — A RULE THAT MATCHES NOTHING READS AS A CAP THAT IS STILL
    PROTECTING SOMETHING, AND THE NEXT SEAT BUDGETS HEIGHT AGAINST A GUARD THAT NO LONGER RUNS. ***
    ⚠ THE LESSON IT ENCODES IS NOT DELETED, ONLY MOVED: the reason the owner *"could not see"* the
    V-split was chrome height, never order. The V-split's own size is now a declared ratio of its
    chart (`VSPLIT_RATIO`) rather than a scroll cap on somebody else's overflow.
    ⚠ `--pane-body-max` STAYS DECLARED in tokens.css with its rationale struck — removing a token
    is a separate cut with its own gate, and `check:tokens` grades the NAME, so an unconsumed
    declaration is inert while a dangling `var()` would not be.
  */
  .alignment {
    display: block;
    margin: 0 0 6px;
    color: var(--muted);
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-align: right;
  }
  /* Density is a real difference, not a label: Scott's workspace breathes, Brucy's packs in.
     Stamped on <html> by workspace.ts so panels inherit it without prop-drilling. */
  :global(html[data-density="comfortable"]) main {
    gap: 20px;
  }
  @media (max-width: 1150px) {
    main {
      grid-template-columns: minmax(0, 1fr);
    }
    .cell.ladder {
      contain: none;
      align-self: start;
      width: 100%;
      overflow: visible;
    }
    :global(.cell.ladder > section),
    :global(.cell.ladder > section.gex2),
    :global(.cell.ladder > section.card),
    :global(.cell.ladder > section.flowrail),
    :global(.cell.ladder .body),
    :global(.cell.ladder .laddermeasure) {
      flex: 0 0 auto;
      overflow: visible;
    }
    :global(.cell.ladder .ladderview) {
      flex: 0 0 auto;
      max-height: 50dvh;
      overflow-y: auto;
    }
    /**
       ⛔⛔ ONE RULE OVER THE CLASS, NOT ONE RULE PER PANEL — R784①, AND THE OWNER PAID FOR THIS.
       This block used to name each cell (`.primary`, `.pane-a`, … `.news`) with an explicit
       `grid-row`, under a comment reading *"EVERY CELL RENDERED MUST APPEAR HERE."* Ten cells were
       rendered and seven rules were written: `flow` and `netprem` were never added.

       MEASURED ON THE LIVE APEX AT 390x844, 2026-08-26, before this change:
         tracks ** "0px 336px" ** — `netprem` kept its DESKTOP inline `grid-column: 2 / span 1`,
         the grid minted an IMPLICIT second track at its content width (336px), that track ate the
         container, and `minmax(0, 1fr)` resolved to ** 0px **.
         ⇒ ** 8 OF 9 PANELS RENDERED ZERO-WIDTH ON THE OWNER'S PHONE. **

       ⭐ THE OLD COMMENT PREDICTED THE PLACEMENT ("column 2 or 3 of a grid that has one track")
       AND NOT THE STARVATION — which is the whole distance between cosmetic and unreadable.
       ⇒ *** A COMMENT THAT NAMES A RISK IS NOT AN ARM, AND A RULE THAT MUST BE MAINTAINED IN
       LOCKSTEP WITH A LIST WILL FALL OUT OF STEP WITH IT. *** A NEGATIVE list (name each cell)
       arms the instances; this POSITIVE one arms the class: no panel added later can be omitted,
       because none is named.

       ⚠ `grid-row: auto` REPLACES THE HAND-NUMBERED ROWS DELIBERATELY. The old block's "never
       auto-flow" was written when the numbering matched DOM order anyway; auto placement in a
       single track IS DOM order, so the reading order is unchanged — and it cannot silently
       collide. It also cures a SECOND defect the first one was hiding: `.primary` matched BOTH
       R771 chart cells and pinned them to `grid-row: 1`, so CHART A and CHART B occupied ONE slot
       (measured: they overlap 350x578px the moment the 0px collapse is cured). ⇒ *** FIXING ONE
       DEFECT CAN UNMASK ANOTHER. ***
       Armed by `scripts/check-mobile.mjs` (single track · no overlap · no sideways scroll · both
       themes) — the arm this block never had. */
    :global(main > .cell),
    :global(main > .cockpit-panels > .cell) {
      grid-column: 1 / span 1 !important;
      grid-row: auto !important;
    }
    /* The tab row is the one piece of chrome that will not wrap on its own: measured 391px wide
       with its right edge at 419 in a 390px viewport, so it alone scrolled the document sideways.
       `.top` already wraps; its child did not. */
    .tabs { flex-wrap: wrap; }
  }
  /* Chrome type scale is 15 / 11 — and deliberately only two steps. It was 15/12/11/10, four sizes
     inside one band where a 1px difference is not a hierarchy, it is noise. The shell's chrome should
     recede so the data leads; tabs, workspace and meta are separated by WEIGHT and COLOUR, which a
     reader can actually perceive, rather than by a step they cannot. */
  .foot {
    margin: 10px auto 0;
    padding: 0 20px;
    color: var(--faint);
    font-size: 11px;
    display: flex;
    gap: 18px;
    flex-wrap: wrap;
  }
</style>
